import { ButtonVariant } from '../../atoms/button/button';
import { resolveStrapiMediaUrl } from '../../../core/utils/strapi-media.util';
import { HeroSectionAction } from './hero-section';

/**
 * Transforms a raw Strapi dynamic zone hero-section entry into the
 * Record<string, unknown> that NgComponentOutlet needs to satisfy
 * HeroSectionComponent's input() contract.
 */
export function adaptHeroSection(
  entry: Record<string, unknown>,
  baseUrl: string,
): Record<string, unknown> {
  const bgImage = entry['backgroundImage'] as Record<string, unknown> | null;
  const resolvedBg =
    resolveStrapiMediaUrl(baseUrl, (bgImage?.['url'] as string) ?? null) ?? '';
  const bgWidth = (bgImage?.['width'] as number) ?? 0;
  const bgHeight = (bgImage?.['height'] as number) ?? 0;
  const bgAlt = (bgImage?.['alternativeText'] as string) ?? '';

  const rawActions = entry['actions'];
  const actions: HeroSectionAction[] = Array.isArray(rawActions)
    ? rawActions.map(
        (a: Record<string, unknown>) =>
          ({
            label: (a['label'] as string) ?? '',
            href: (a['href'] as string) ?? (a['url'] as string) ?? '#',
            variant: ((a['variant'] as string) ?? 'primary') as ButtonVariant,
          }) satisfies HeroSectionAction,
      )
    : [];

  const rawStats = entry['stats'];
  const stats = Array.isArray(rawStats)
    ? rawStats.map((s: Record<string, unknown>) => ({
        value: (s['value'] as string) ?? '',
        label: (s['label'] as string) ?? '',
      }))
    : [];

  // Spec 31: h1Source enum controls which hero field wraps in <h1>.
  // Narrow unknown → 'overline' | 'heading'; anything other than literal
  // 'heading' (incl. undefined, null, stray strings) falls back to 'overline'
  // so older Strapi entries without the field keep today's behaviour.
  const rawH1Source = entry['h1Source'];
  const h1Source: 'overline' | 'heading' =
    rawH1Source === 'heading' ? 'heading' : 'overline';

  return {
    sectionId: (entry['htmlId'] as string) ?? '',
    heading: (entry['heading'] as string) ?? '',
    subtitle: (entry['subtitle'] as string) ?? '',
    overline: (entry['overline'] as string) ?? '',
    lead: (entry['lead'] as string) ?? '',
    backgroundImage: resolvedBg,
    backgroundImageWidth: bgWidth,
    backgroundImageHeight: bgHeight,
    backgroundImageAlt: bgAlt,
    variant: (entry['variant'] as string) === 'image' || bgImage ? 'image' : '',
    h1Source,
    actions,
    stats,
    cssClass: (entry['cssClass'] as string) ?? '',
  };
}
